import { Select, Input } from "antd";
import SentryWrap from "components/Sentry";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shuffle } from "lodash";
import { formatTextForRemoveUnderScore } from "../helper";
import * as Sentry from "@sentry/react";
import Acent from "components/Acent";
import styled from "styled-components";

/* ─── Styled Components ──────────────────────────────────────────── */

const Wrapper = styled.div`
  font-family: "Georgia", serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  position: absolute;
  top: 0px;
  right: 0;
`;

const HeaderLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #03989e;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
`;

const ProgressBar = styled.div`
  width: 80px;
  height: 4px;
  background: white;
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #03999e;
  border-radius: 2px;
  transition: width 0.35s ease;
  width: ${({ $pct }) => $pct}%;
`;

const TextContainer = styled.div`
  font-size: 16px;
  /* Base line-height for plain text lines */
  line-height: 1.8;
  color: #1f2937;

  ol,
  ul {
    padding-left: 30px;
    margin: 10px 0;
  }
  ol { list-style-type: decimal; }
  ul { list-style-type: disc; }
  li { margin: 5px 0; line-height: 1.8; }
  span { margin: 0; }

  p {
    /* Generous line-height so inline selects/inputs don't clip neighbouring lines */
    line-height: 2.4;
    margin: 0 0 1rem;
  }
`;

const BlankWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 3px;
  vertical-align: middle;
`;


/* Ant Design Select overrides */
const StyledSelectWrapper = styled.span`
  .ant-select .ant-select-selector {
    min-width: 120px;
    height: 30px !important;
    padding: 0 10px !important;
    font-size: 14px !important;
    font-family: "Georgia", serif;
    font-weight: 600;
    border-radius: 6px !important;
    border: 1.5px solid ${({ $answered }) => ($answered ? "#fff" : "#d1d5db")} !important;
    background: ${({ $answered }) => ($answered ? "#fff" : "#ffffff")} !important;
    color: ${({ $answered }) => ($answered ? "#000" : "#374151")} !important;
    transition: border-color 0.2s, background 0.2s;
    box-shadow: none !important;
     border-color: #1d9e75 !important;
  }

  .ant-select .ant-select-selector:hover {
    border-color: #1d9e75 !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: #0f6e56 !important;
    box-shadow: 0 0 0 2px rgba(15, 110, 86, 0.12) !important;
  }

  .ant-select-selection-placeholder {
    color: #9ca3af !important;
    font-weight: 400;
    line-height: 28px !important;
  }

  .ant-select-selection-item {
    line-height: 28px !important;
  }

  .ant-select-arrow {
    color: ${({ $answered }) => ($answered ? "#000" : "#9ca3af")};
    font-size: 10px;
  }
`;

const InputWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  vertical-align: middle;

  .ant-input {
    width: 120px;
    height: 30px;
    font-size: 14px;
    font-family: "Georgia", serif;
    font-weight: 600;
    border-radius: 6px;
    border: 1.5px solid ${({ $answered }) => ($answered ? "#0f6e56" : "#d1d5db")};
    background: ${({ $answered }) => ($answered ? "#e1f5ee" : "#ffffff")};
    color: ${({ $answered }) => ($answered ? "#085041" : "#374151")};
    padding: 0 10px;
    transition: border-color 0.2s, background 0.2s;

    &:hover { border-color: #1d9e75; }
    &:focus {
      border-color: #0f6e56;
      box-shadow: 0 0 0 2px rgba(15, 110, 86, 0.12);
      outline: none;
    }
  }
`;

/* ─── Component ──────────────────────────────────────────────────── */

const StudentFillInTheBlanks = ({
  text,
  highlights,
  answerOption,
  onChangeHandler,
  selected_answer = [],
  response_type,
  showProgress = true,
  progressTotalBlanks = 0,
  progressAnsweredCount = 0,
}) => {
  const [shuffledAnswerOption, setShuffledAnswerOption] = useState([]);
  const [selectDropdownOpenCount, setSelectDropdownOpenCount] = useState(0);
  const inputRef = useRef([]);
  const lastShuffledSignatureRef = useRef(null);

  const answerOptionSignature = useMemo(() => {
    if (!Array.isArray(answerOption) || answerOption.length === 0) return "";
    return answerOption.map((a) => String(a?.text ?? "")).join("\u0001");
  }, [answerOption]);

  const handleSelectOpenChange = useCallback((open) => {
    setSelectDropdownOpenCount((c) => {
      const next = c + (open ? 1 : -1);
      return next < 0 ? 0 : next;
    });
  }, []);

  useEffect(() => {
    if (selectDropdownOpenCount > 0) return;
    if (lastShuffledSignatureRef.current === answerOptionSignature) return;
    lastShuffledSignatureRef.current = answerOptionSignature;
    if (!answerOptionSignature) {
      setShuffledAnswerOption([]);
      return;
    }
    setShuffledAnswerOption(shuffle(answerOption) || []);
  }, [answerOption, answerOptionSignature, selectDropdownOpenCount]);

  /* progress */
  const totalBlanks = Number.isFinite(progressTotalBlanks) ? progressTotalBlanks : (highlights?.flat()?.length ?? 0);
  const answeredCount = Number.isFinite(progressAnsweredCount) ? progressAnsweredCount : selected_answer.filter((a) => a?.answer).length;
  const progressPct = totalBlanks > 0 ? Math.round((answeredCount / totalBlanks) * 100) : 0;

  /* ── helpers ── */
  const stripHtmlTags = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").replace(/[\u200B-\u200D\uFEFF]/g, "");
  };

  // Returns true only when the ENTIRE htmlContent is one complete <p>…</p>
  // with no trailing content — meaning it was never split around a blank.
  const isCompleteP = (htmlContent) => {
    const trimmed = htmlContent.trim();
    if (!/^<p[\s>]/i.test(trimmed)) return false;
    // Count opening vs closing <p> tags to make sure it closes exactly once
    const opens = (trimmed.match(/<p[\s>]/gi) || []).length;
    const closes = (trimmed.match(/<\/p>/gi) || []).length;
    return opens === 1 && closes === 1 && trimmed.endsWith("</p>");
  };

  // Converts a CSS style string like "color: rgb(0,0,0); font-weight: bold"
  // into a React style object like { color: "rgb(0,0,0)", fontWeight: "bold" }
  const parseStyleString = (styleStr) => {
    if (!styleStr) return undefined;
    const obj = {};
    styleStr.split(";").forEach((decl) => {
      const [prop, ...rest] = decl.split(":");
      if (!prop || !rest.length) return;
      const key = prop.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      obj[key] = rest.join(":").trim();
    });
    return Object.keys(obj).length ? obj : undefined;
  };

  // Recursively walk a DOM node and render all its text/inline content as
  // React <span> elements, so we never accidentally create block elements
  // from a partial HTML fragment.
  const flattenNodeToSpans = (node, keyPrefix) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent;
      // Keep &nbsp; as a real space character; skip truly empty strings
      if (!t) return null;
      if (t.replace(/\u00a0/g, "").trim() === "" && !t.includes("\u00a0")) return null;
      return <span key={keyPrefix}>{t}</span>;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.nodeName.toLowerCase();
      // <br> → stays as <br> for line breaks inside a stanza
      if (tag === "br") return <br key={keyPrefix} />;
      // For all other elements, recurse into children so we preserve
      // inline styling (color, bold, etc.) without creating block elements
      const children = Array.from(node.childNodes)
        .map((child, i) => flattenNodeToSpans(child, `${keyPrefix}-${i}`))
        .filter(Boolean);
      if (children.length === 0) return null;
      // Pass through inline styles (e.g. color) via dangerouslySetInnerHTML
      // on a wrapper span so CSS like color:rgb(0,0,0) is preserved
      const styleAttr = node.getAttribute("style");
      return (
        <span key={keyPrefix} style={styleAttr ? parseStyleString(styleAttr) : undefined}>
          {children}
        </span>
      );
    }
    return null;
  };

  const parseHtmlToSpans = (htmlContent, segmentIdx) => {
    if (!htmlContent) return [];
    const div = document.createElement("div");
    div.innerHTML = htmlContent;

    const complete = isCompleteP(htmlContent.trim());

    return Array.from(div.childNodes)
      .map((node, nodeIdx) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const t = node.textContent;
          if (!t || t.trim() === "") return null;
          return <span key={`html-${segmentIdx}-text-${nodeIdx}`}>{t}</span>;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tag = node.nodeName.toLowerCase();

          // A complete, un-split <p> → render as a proper block paragraph
          if (tag === "p" && complete) {
            return (
              <p
                key={`html-${segmentIdx}-p-${nodeIdx}`}
                dangerouslySetInnerHTML={{ __html: node.innerHTML }}
                style={{ margin: "0 0 1.2rem" }}
              />
            );
          }

          // A <p> that was SPLIT around a blank — the browser auto-closes
          // the unclosed tag into a block, which pushes the dropdown onto
          // the next line. Fix: unwrap the <p> and render its children inline.
          if (tag === "p" && !complete) {
            return Array.from(node.childNodes)
              .map((child, childIdx) =>
                flattenNodeToSpans(child, `html-${segmentIdx}-unwrapped-${nodeIdx}-${childIdx}`)
              )
              .filter(Boolean);
          }

          // Any other element (span, strong, em, etc.) → render as-is inline
          return (
            <span
              key={`html-${segmentIdx}-element-${nodeIdx}`}
              dangerouslySetInnerHTML={{ __html: node.outerHTML }}
            />
          );
        }
        return null;
      })
      .flat()
      .filter(Boolean);
  };

  /* ── blank renderers ── */
  const renderSelect = (index) => {
    const isAnswered = !!selected_answer[index]?.answer;
    return (
      <BlankWrapper key={`blank-${index}`}>
        <StyledSelectWrapper $answered={isAnswered}>
          <Select
            placeholder="choose…"
            onChange={(value) => onChangeHandler(value, index)}
            value={selected_answer[index]?.answer || undefined}
            placement="bottomLeft"
            getPopupContainer={(trigger) => trigger.parentNode}
            onOpenChange={handleSelectOpenChange}
          >
            {shuffledAnswerOption.map((ans, idx) => (
              <Select.Option key={idx} value={ans?.text}>
                {formatTextForRemoveUnderScore(ans?.text)}
              </Select.Option>
            ))}
          </Select>
        </StyledSelectWrapper>
      </BlankWrapper>
    );
  };

  const renderInput = (index) => {
    const isAnswered = !!selected_answer[index]?.answer;
    return (
      <BlankWrapper key={`blank-${index}`}>
        <InputWrapper $answered={isAnswered}>
          <Input
            ref={(el) => {
              if (el) {
                if (Array.isArray(inputRef.current)) {
                  inputRef.current[index] = el;
                } else {
                  inputRef.current = [];
                  inputRef.current[index] = el;
                }
              }
            }}
            onChange={(e) => onChangeHandler(e.target.value, index)}
            value={selected_answer[index]?.answer || ""}
          />
          <Acent
            inputRef={inputRef.current ? inputRef.current[index]?.input : null}
            setValue={(value) => onChangeHandler(value, index)}
          />
        </InputWrapper>
      </BlankWrapper>
    );
  };

  /* ── plain-text path ── */
  const generateBlanksText = (text, highlights) => {
    try {
      let result = [];
      let currentIndex = 0;
      const sorted = highlights?.flat()?.sort((a, b) => a.start - b.start);
      if (sorted) {
        sorted.forEach((highlight, index) => {
          if (highlight.start > currentIndex) {
            result.push(text.slice(currentIndex, highlight.start));
          }
          result.push(response_type === "input" ? renderInput(index) : renderSelect(index));
          currentIndex = highlight.end;
        });
      }
      if (currentIndex < text.length) result.push(text.slice(currentIndex));
      return result;
    } catch (error) {
      Sentry.captureException(`561 Error in generateBlanksText: ${error}`);
      console.log(error);
    }
  };

  /* ── HTML path ── */
  const insertBlanksInHtml = (htmlText, plainText, highlights) => {
    if (
      !highlights ||
      highlights.length === 0 ||
      !Array.isArray(highlights.flat()) ||
      highlights.flat().length === 0
    ) {
      return [{ type: "html", content: htmlText }];
    }

    try {
      const flatHighlights = highlights.flat();
      const sortedHighlights = flatHighlights
        .map((h, idx) => ({ ...h, originalIndex: idx }))
        .sort((a, b) => b.start - a.start);

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlText;

      const insertPlaceholder = (node, plainIndex = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const originalNodeText = node.textContent || "";
          const normalizedNodeText = originalNodeText.replace(/[\u200B-\u200D\uFEFF]/g, "");
          const nodeStart = plainIndex;
          const nodeEnd = plainIndex + normalizedNodeText.length;

          const highlightsInNode = [];
          for (let i = sortedHighlights.length - 1; i >= 0; i--) {
            const highlight = sortedHighlights[i];
            if (highlight.start < nodeEnd && highlight.end > nodeStart) {
              highlightsInNode.push({ highlight, sortedIndex: i });
            }
          }

          if (highlightsInNode.length > 0) {
            highlightsInNode.sort((a, b) => a.highlight.start - b.highlight.start);
            const fragment = document.createDocumentFragment();
            let currentNormalizedPos = 0;
            let currentOriginalPos = 0;
            const indicesToRemove = [];

            for (const { highlight, sortedIndex } of highlightsInNode) {
              let relativeStart = Math.max(0, highlight.start - nodeStart);
              let relativeEnd = Math.min(normalizedNodeText.length, highlight.end - nodeStart);

              const expectedText = highlight.text
                ? highlight.text.replace(/[\u200B-\u200D\uFEFF]/g, "")
                : null;
              if (expectedText) {
                const actual = normalizedNodeText.substring(relativeStart, relativeEnd);
                if (actual !== expectedText) {
                  const searchStart = Math.max(
                    0,
                    Math.max(relativeStart - expectedText.length - 2, currentNormalizedPos)
                  );
                  const searchEnd = Math.min(
                    normalizedNodeText.length,
                    relativeEnd + expectedText.length + 2
                  );
                  const area = normalizedNodeText.substring(searchStart, searchEnd);
                  const found = area.indexOf(expectedText);
                  if (found !== -1) {
                    relativeStart = searchStart + found;
                    relativeEnd = relativeStart + expectedText.length;
                  }
                }
              }

              if (relativeStart > currentNormalizedPos) {
                const beforeLen = relativeStart - currentNormalizedPos;
                let nCount = 0;
                let beforeEnd = currentOriginalPos;
                for (let i = currentOriginalPos; i < originalNodeText.length; i++) {
                  if (!/[\u200B-\u200D\uFEFF]/.test(originalNodeText[i])) {
                    if (nCount >= beforeLen) break;
                    nCount++;
                  }
                  beforeEnd = i + 1;
                }
                const beforeText = originalNodeText.substring(currentOriginalPos, beforeEnd);
                if (beforeText) fragment.appendChild(document.createTextNode(beforeText));
                currentOriginalPos = beforeEnd;
                currentNormalizedPos = relativeStart;
              }

              const hlLen = relativeEnd - relativeStart;
              let nCount = 0;
              let hlEnd = currentOriginalPos;
              for (let i = currentOriginalPos; i < originalNodeText.length; i++) {
                if (!/[\u200B-\u200D\uFEFF]/.test(originalNodeText[i])) {
                  if (nCount >= hlLen) break;
                  nCount++;
                }
                hlEnd = i + 1;
              }

              currentOriginalPos = hlEnd;
              currentNormalizedPos = relativeEnd;

              const placeholder = document.createElement("span");
              placeholder.setAttribute("data-blank-index", highlight.originalIndex);
              placeholder.setAttribute("data-placeholder", "BLANK_COMPONENT");
              fragment.appendChild(placeholder);
              indicesToRemove.push(sortedIndex);
            }

            if (currentNormalizedPos < normalizedNodeText.length) {
              const afterLen = normalizedNodeText.length - currentNormalizedPos;
              let nCount = 0;
              let afterEnd = currentOriginalPos;
              for (let i = currentOriginalPos; i < originalNodeText.length && nCount < afterLen; i++) {
                if (!/[\u200B-\u200D\uFEFF]/.test(originalNodeText[i])) nCount++;
                afterEnd = i + 1;
              }
              const afterText = originalNodeText.substring(currentOriginalPos, afterEnd);
              if (afterText) fragment.appendChild(document.createTextNode(afterText));
            }

            indicesToRemove.sort((a, b) => b - a);
            for (const idx of indicesToRemove) sortedHighlights.splice(idx, 1);
            node.parentNode.replaceChild(fragment, node);
            return nodeEnd;
          }
          return nodeEnd;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          let currentIndex = plainIndex;
          for (const child of Array.from(node.childNodes)) {
            currentIndex = insertPlaceholder(child, currentIndex);
          }
          return currentIndex;
        }
        return plainIndex;
      };

      insertPlaceholder(tempDiv);

      const segments = [];
      const html = tempDiv.innerHTML;
      const re = /<span data-blank-index="(\d+)" data-placeholder="BLANK_COMPONENT"><\/span>/g;
      let lastIndex = 0;
      let match;
      let seqIdx = 0;

      while ((match = re.exec(html)) !== null) {
        if (match.index > lastIndex) {
          const seg = html.substring(lastIndex, match.index);
          if (seg) segments.push({ type: "html", content: seg });
        }
        const index = seqIdx++;
        segments.push({
          type: "blank",
          component: response_type === "input" ? renderInput(index) : renderSelect(index),
        });
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < html.length) {
        const seg = html.substring(lastIndex);
        if (seg) segments.push({ type: "html", content: seg });
      }

      return segments.length > 0 ? segments : [{ type: "html", content: htmlText }];
    } catch (error) {
      Sentry.captureException(`562 Error in insertBlanksInHtml: ${error}`);
      console.log(error);
      return [{ type: "html", content: htmlText }];
    }
  };

  /* ── render ── */
  const hasHtml = /<[^>]+>/.test(text || "");
  const plainText = hasHtml ? stripHtmlTags(text) : text;

  let processedText;
  if (hasHtml && text) {
    const segments = insertBlanksInHtml(text, plainText, highlights);
    processedText = segments
      .map((seg, idx) =>
        seg.type === "html" ? parseHtmlToSpans(seg.content, idx) : seg.component
      )
      .flat();
  } else {
    processedText = generateBlanksText(plainText, highlights);
  }

  return (
    <SentryWrap>
      <Wrapper>
        {/* ── Header with progress ── */}
        <Header>
          <HeaderLabel></HeaderLabel>
          {showProgress ? (
            <ProgressRow>
              Progress
              <ProgressBar>
                <ProgressFill $pct={progressPct} />
              </ProgressBar>
              <span>
                {answeredCount} / {totalBlanks}
              </span>
            </ProgressRow>
          ) : null}
        </Header>

        {/* ── Poem / text body ── */}
        <TextContainer>{processedText}</TextContainer>
      </Wrapper>
    </SentryWrap>
  );
};

export default memo(StudentFillInTheBlanks);